import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';
import SignatureCanvas from 'react-signature-canvas';
import { CheckCircle, ArrowUp, ArrowDown, Shield, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { sendQuoteEmail } from '@/lib/emailService';

interface ServiceTier {
  id: string;
  name: string;
  description: string;
  price: number;
  features: string[];
  isRecommended?: boolean;
  duration?: number;
}

interface QuoteData {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  customer_address?: string;
  total_amount: number;
  status: string;
  approved: boolean;
  approved_at?: string;
  approved_by?: string;
  signature?: string;
  notes?: string;
  created_at: string;
  valid_until: string;
  is_interactive: boolean;
  recommended_service_id?: string;
  service_tiers?: ServiceTier[];
  selected_tier_id?: string;
}

export function InteractiveQuoteView() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const { config } = useBusinessConfig();
  const [quote, setQuote] = useState<QuoteData | null>(null);
  const [selectedTier, setSelectedTier] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [showToT, setShowToT] = useState(false);
  const sigCanvas = useRef<SignatureCanvas>(null);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const { data, error } = await supabase
          .from('quotes')
          .select('*')
          .eq('id', quoteId)
          .single();

        if (error) throw error;
        setQuote(data);
        
        // Set default selected tier
        if (data.service_tiers && data.service_tiers.length > 0) {
          const recommended = data.service_tiers.find((t: ServiceTier) => t.isRecommended);
          setSelectedTier(recommended?.id || data.service_tiers[0].id);
        }
      } catch (error) {
        console.error('Error fetching quote:', error);
        toast.error('Quote not found');
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [quoteId]);

  const handleTierSelect = (tierId: string) => {
    setSelectedTier(tierId);
  };

  const handleApprove = async () => {
    if (!quoteId || !selectedTier) return;

    setApproving(true);
    try {
      const selectedTierData = quote?.service_tiers?.find(t => t.id === selectedTier);
      if (!selectedTierData) throw new Error('Invalid tier selected');

      const signatureDataUrl = sigCanvas.current?.toDataURL() || null;

      // Update quote with approval and selected tier
      const { error } = await supabase
        .from('quotes')
        .update({
          approved: true,
          approved_at: new Date().toISOString(),
          approved_by: quote?.customer_name || 'Customer',
          signature: signatureDataUrl,
          status: 'approved',
          total_amount: selectedTierData.price,
          selected_tier_id: selectedTier,
        })
        .eq('id', quoteId);

      if (error) throw error;

      toast.success('Quote approved successfully!');

      // Send email notification
      try {
        if (config && quote) {
          await sendQuoteEmail(
            quote.customer_email,
            {
              quoteNumber: quote.id.slice(0, 8).toUpperCase(),
              customerName: quote.customer_name,
              totalAmount: selectedTierData.price,
              businessName: config.biz_name || '',
            }
          );
          toast.success('Confirmation sent to your email!');
        }
      } catch (emailError) {
        console.error('Email sending failed:', emailError);
      }

      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      console.error('Error approving quote:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to approve quote');
    } finally {
      setApproving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-slate-800">Quote Not Found</h2>
          <Button onClick={() => window.location.href = '/'} className="mt-6">
            Return Home
          </Button>
        </div>
      </div>
    );
  }

  const selectedTierData = quote.service_tiers?.find(t => t.id === selectedTier);
  const subtotal = selectedTierData?.price || 0;
  const gst = config?.gst_registered ? subtotal * 0.1 : 0;
  const total = subtotal + gst;

  return (
    <div className="min-h-screen bg-slate-50 pb-24 font-sans">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b z-50 shadow-sm">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => window.history.back()}>
            ← Back
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-900 rounded-xl flex items-center justify-center">
              <span className="text-white text-xs font-black">
                {config?.biz_name?.charAt(0) || 'T'}
              </span>
            </div>
            <div className="text-left">
              <p className="font-bold text-slate-900 text-sm">{config?.biz_name}</p>
              <p className="text-[10px] text-slate-500">Interactive Quote</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Quote Info */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h1 className="text-2xl font-black text-slate-900 uppercase mb-4">
            Choose Your Service
          </h1>
          <p className="text-slate-600 mb-6">
            Hi {quote.customer_name}! Select the service tier that works best for you. 
            You can upgrade or downgrade anytime.
          </p>

          {/* Service Tiers */}
          <div className="space-y-4">
            {quote.service_tiers?.map((tier) => (
              <div
                key={tier.id}
                onClick={() => handleTierSelect(tier.id)}
                className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all ${
                  selectedTier === tier.id
                    ? tier.isRecommended
                      ? 'border-purple-600 bg-purple-50 shadow-lg'
                      : 'border-slate-900 bg-slate-50 shadow-lg'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                {tier.isRecommended && (
                  <div className="absolute -top-3 left-6 bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-black uppercase flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Recommended
                  </div>
                )}
                
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="text-xl font-black text-slate-900">{tier.name}</h3>
                    <p className="text-sm text-slate-600 mt-1">{tier.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-black text-slate-900">${tier.price.toFixed(2)}</p>
                    {tier.duration && (
                      <p className="text-xs text-slate-500">{tier.duration} min</p>
                    )}
                  </div>
                </div>

                <ul className="space-y-2">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {selectedTier === tier.id && (
                  <div className="absolute top-4 right-4">
                    <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Upgrade/Downgrade Info */}
        {selectedTier && selectedTier !== quote.recommended_service_id && selectedTierData && (
          <div className={`p-4 rounded-xl border-2 ${
            selectedTierData.price > (quote.service_tiers?.find(t => t.isRecommended)?.price || 0)
              ? 'bg-green-50 border-green-200'
              : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="flex items-center gap-2">
              {selectedTierData.price > (quote.service_tiers?.find(t => t.isRecommended)?.price || 0) ? (
                <ArrowUp className="w-5 h-5 text-green-600" />
              ) : (
                <ArrowDown className="w-5 h-5 text-amber-600" />
              )}
              <p className={`font-bold ${
                selectedTierData.price > (quote.service_tiers?.find(t => t.isRecommended)?.price || 0)
                  ? 'text-green-800'
                  : 'text-amber-800'
              }`}>
                {selectedTierData.price > (quote.service_tiers?.find(t => t.isRecommended)?.price || 0)
                  ? '🎉 Upgrading! You\'ll get extra features.'
                  : '💰 Downgrading. You can upgrade anytime.'}
              </p>
            </div>
          </div>
        )}

        {/* Terms of Trade */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <button
            onClick={() => setShowToT(!showToT)}
            className="flex items-center gap-2 font-black text-slate-900 uppercase mb-4"
          >
            <FileText className="w-5 h-5" />
            Terms of Trade
            <span className="text-slate-400 text-sm">({showToT ? 'hide' : 'show'})</span>
          </button>

          {showToT && (
            <div className="prose prose-sm max-w-none">
              <DynamicTermsOfTrade config={config as unknown as Record<string, string | number | boolean | undefined> | null} selectedTier={selectedTierData} />
            </div>
          )}
        </div>

        {/* Signature */}
        {!quote.approved && (
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <h3 className="font-black text-slate-900 uppercase mb-4">Your Signature</h3>
            <div className="border-2 border-dashed border-slate-300 rounded-xl overflow-hidden">
              <SignatureCanvas
                ref={sigCanvas}
                penColor='black'
                canvasProps={{
                  width: 600,
                  height: 150,
                  className: 'w-full h-32 bg-white'
                }}
              />
            </div>
            <Button
              onClick={() => sigCanvas.current?.clear()}
              variant="ghost"
              size="sm"
              className="mt-2"
            >
              Clear Signature
            </Button>
          </div>
        )}

        {/* Approval Section */}
        {!quote.approved ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <p className="font-bold text-blue-800 text-sm flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Dual Approval Required
              </p>
              <p className="text-blue-700 text-sm mt-1">
                This quote requires approval from both you and your support worker before proceeding.
              </p>
            </div>

            <Button
              onClick={handleApprove}
              disabled={approving || !selectedTier}
              className="w-full h-14 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-black uppercase tracking-widest text-xs flex gap-2"
            >
              {approving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Approve Quote (${total.toFixed(2)})
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <h2 className="font-black text-green-800 uppercase">Quote Approved!</h2>
                <p className="text-green-700 text-sm">
                  Approved on {new Date(quote.approved_at!).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm">
          <p>Questions? Contact {config?.contact_email} or {config?.contact_phone}</p>
          <p className="text-xs mt-2">Quote valid until {new Date(quote.valid_until).toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
}

// Dynamic Terms of Trade Component
function DynamicTermsOfTrade({ config, selectedTier }: { config: Record<string, string | number | boolean | undefined> | null; selectedTier?: ServiceTier }) {
  if (!config) return null;

  return (
    <div className="space-y-4 text-sm">
      <div className="bg-slate-50 p-4 rounded-lg">
        <h4 className="font-bold text-slate-900 mb-2">Business Details</h4>
        <p><strong>Business Name:</strong> {String(config.biz_name || '')}</p>
        {config.abn && <p><strong>ABN:</strong> {String(config.abn)}</p>}
        {config.contact_email && <p><strong>Email:</strong> {String(config.contact_email)}</p>}
        {config.contact_phone && <p><strong>Phone:</strong> {String(config.contact_phone)}</p>}
      </div>

      <div className="bg-slate-50 p-4 rounded-lg">
        <h4 className="font-bold text-slate-900 mb-2">Service Details</h4>
        {selectedTier && (
          <>
            <p><strong>Service:</strong> {selectedTier.name}</p>
            <p><strong>Price:</strong> ${selectedTier.price.toFixed(2)} {config.gst_registered ? '(inc. GST)' : '(ex. GST)'}</p>
            {selectedTier.duration && <p><strong>Duration:</strong> {selectedTier.duration} minutes</p>}
          </>
        )}
      </div>

      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
        <h4 className="font-bold text-amber-900 mb-2">⚠️ PPSR & Debt Collection Notice</h4>
        <p className="text-amber-800 mb-2">
          This business is registered with the <strong>Personal Property Securities Register (PPSR)</strong> for debt collection purposes.
        </p>
        <ul className="list-disc list-inside space-y-1 text-amber-800">
          <li>Payment is due on completion of service</li>
          <li>Overdue accounts may be referred to debt collection</li>
          <li>Additional fees may apply for collection services</li>
          <li>Your rights as a consumer are protected under Australian law</li>
        </ul>
      </div>

      <div className="bg-slate-50 p-4 rounded-lg">
        <h4 className="font-bold text-slate-900 mb-2">Payment Terms</h4>
        <p className="mb-2">Payment methods accepted:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Bank Transfer (details below)</li>
          <li>Cash</li>
          <li>Credit Card (surcharge may apply)</li>
        </ul>
        {config.bank_account_name && (
          <div className="mt-3 pt-3 border-t">
            <p className="font-bold">Bank Details:</p>
            <p>Account: {String(config.bank_account_name)}</p>
            {config.bank_account_bsb && <p>BSB: {String(config.bank_account_bsb)}</p>}
            {config.bank_account_number && <p>Account No: {String(config.bank_account_number)}</p>}
          </div>
        )}
      </div>

      <div className="text-xs text-slate-500">
        <p>By approving this quote, you agree to the Terms of Trade outlined above.</p>
      </div>
    </div>
  );
}